import type { QueryClient } from '@tanstack/react-query';
import type { RealtimeEvent } from '@cyphex/types';
import { QUERY_KEYS } from '@/constants/queryKeys';
import { CHANNEL_KEYS } from '@/features/channels/api/queryKeys';
import { TEAM_KEYS } from '@/features/teams/api/queryKeys';

/**
 * Structural changes — a team renamed, a channel you were removed from, an
 * unread counter moving — are INVALIDATIONS, not cache surgery.
 *
 * The distinction matters: a message is a leaf the client can append safely,
 * but membership decides what the client is even allowed to see, so the server
 * must be asked again rather than guessed at from a delta.
 */
export function applyEventToShell(client: QueryClient, event: RealtimeEvent): void {
  const invalidateSidebar = (): void => {
    void client.invalidateQueries({ queryKey: QUERY_KEYS.sidebar() });
  };

  switch (event.type) {
    case 'unread.changed':
      invalidateSidebar();
      break;

    /* The Activity rail badge comes from the SIDEBAR's counts, not these - both
       have to move, or "Mark all as read" leaves the red badge standing. */
    case 'notification.push':
    case 'notification.dismissed':
    case 'notification.read':
    case 'notification.seen':
      void client.invalidateQueries({ queryKey: QUERY_KEYS.notificationCounts() });
      invalidateSidebar();
      break;

    case 'team.created':
    case 'team.updated':
    case 'team.archived':
    case 'team.deleted':
      void client.invalidateQueries({ queryKey: TEAM_KEYS.all });
      invalidateSidebar();
      break;

    case 'team.member.added':
    case 'team.member.removed':
    case 'team.member.role_changed':
      void client.invalidateQueries({ queryKey: TEAM_KEYS.members(event.teamId) });
      invalidateSidebar();
      break;

    case 'channel.created':
    case 'channel.updated':
    case 'channel.archived':
    case 'channel.deleted':
      void client.invalidateQueries({ queryKey: CHANNEL_KEYS.detail(event.channelId) });
      void client.invalidateQueries({ queryKey: TEAM_KEYS.all });
      invalidateSidebar();
      break;

    case 'channel.member.added':
    case 'channel.member.removed':
    case 'channel.member.role_changed':
      void client.invalidateQueries({ queryKey: CHANNEL_KEYS.members(event.channelId) });
      invalidateSidebar();
      break;

    /* Permissions changed under us: everything cached may now be wrong. */
    case 'session.permissions_changed':
      void client.invalidateQueries();
      break;

    default:
      break;
  }
}
